import { c as _c } from "react/compiler-runtime";
import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";
export type AuthHeaders = Record<string, string>;
interface WaveReadJwtPayload {
  readonly sub?: string | undefined;
  readonly role?: string | null | undefined;
  readonly exp?: number | undefined;
}
export interface WaveReadVerifiedIdentity {
  readonly addressKey: string;
  readonly activeProfileProxyId: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly identityKey: string;
  readonly jwtExpiresAt: number;
  readonly authHeaders: AuthHeaders;
}
interface WaveReadJwtIdentity {
  readonly addressKey: string;
  readonly proxyCreatorId: string | null;
  readonly jwtExpiresAt: number;
}
export interface WaveReadTemporaryProxyRoleIdentity {
  readonly addressKey: string;
  readonly proxyCreatorId: string;
  readonly identityKey: string;
}
export interface WaveReadIdentityConfig {
  readonly address: string | undefined;
  readonly connectedProfileId: string | null;
  readonly activeProfileProxyId: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly walletAuth: string | null;
}
interface WaveReadIdentityState {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly identityKey: string;
  readonly proxyRoleIdentityKey: string | null;
  readonly temporaryProxyRoleIdentity: WaveReadTemporaryProxyRoleIdentity | undefined;
  readonly verifiedIdentity: WaveReadVerifiedIdentity | undefined;
}
const getAddressKey = (address: string | undefined): string | null => address?.toLowerCase() ?? null;
export const getWaveReadIdentityKey = ({
  addressKey,
  activeProfileProxyId
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
}): string => JSON.stringify([addressKey, activeProfileProxyId]);
export const getWaveReadProxyRoleIdentityKey = ({
  addressKey,
  proxyCreatorId
}: {
  readonly addressKey: string;
  readonly proxyCreatorId: string;
}): string => JSON.stringify([addressKey, "proxy-role", proxyCreatorId]);
export const getWaveReadRequestKey = ({
  addressKey,
  activeProfileProxyId,
  waveId
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly waveId: string;
}): string => JSON.stringify([addressKey, activeProfileProxyId, waveId]);
export const getWaveReadProxyRoleRequestKey = ({
  addressKey,
  proxyCreatorId,
  waveId
}: {
  readonly addressKey: string;
  readonly proxyCreatorId: string;
  readonly waveId: string;
}): string => JSON.stringify([addressKey, "proxy-role", proxyCreatorId, waveId]);
const getAuthHeaders = (walletAuth: string): AuthHeaders => ({
  Authorization: `Bearer ${walletAuth}`
});
export const isWaveReadJwtExpired = (jwtExpiresAt: number): boolean => jwtExpiresAt <= Math.floor(Date.now() / 1000);
const normalizeWaveReadJwtRole = ({
  role,
  connectedProfileId,
  activeProfileProxyId
}: {
  readonly role: string | null | undefined;
  readonly connectedProfileId: string | null;
  readonly activeProfileProxyId: string | null;
}): string | null => {
  if (typeof role !== "string" || role.length === 0) {
    return null;
  }
  if (activeProfileProxyId === null && role === connectedProfileId) {
    return null;
  }
  return role;
};
const decodeWaveReadJwtIdentity = (walletAuth: string | null, connectedProfileId: string | null, activeProfileProxyId: string | null): WaveReadJwtIdentity | undefined => {
  if (!walletAuth) {
    return undefined;
  }
  try {
    const decodedJwt = jwtDecode<WaveReadJwtPayload>(walletAuth);
    const expiresAt = decodedJwt.exp;
    if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt) || isWaveReadJwtExpired(expiresAt)) {
      return undefined;
    }
    const addressKey = decodedJwt.sub?.toLowerCase() ?? null;
    if (!addressKey) {
      return undefined;
    }
    return {
      addressKey,
      proxyCreatorId: normalizeWaveReadJwtRole({
        role: decodedJwt.role,
        connectedProfileId,
        activeProfileProxyId
      }),
      jwtExpiresAt: expiresAt
    };
  } catch {
    return undefined;
  }
};
const getVerifiedAuthHeaders = ({
  walletAuth,
  addressKey,
  jwtIdentity,
  activeProfileProxyCreatorId
}: {
  readonly walletAuth: string | null;
  readonly addressKey: string | null;
  readonly jwtIdentity: WaveReadJwtIdentity | undefined;
  readonly activeProfileProxyCreatorId: string | null;
}): AuthHeaders | undefined => {
  if (!walletAuth || !addressKey || !jwtIdentity) {
    return undefined;
  }
  if (jwtIdentity.addressKey !== addressKey) {
    return undefined;
  }
  if (jwtIdentity.proxyCreatorId !== activeProfileProxyCreatorId) {
    return undefined;
  }
  return getAuthHeaders(walletAuth);
};
const getTemporaryProxyRoleIdentity = ({
  addressKey,
  activeProfileProxyId,
  jwtIdentity
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly jwtIdentity: WaveReadJwtIdentity | undefined;
}): WaveReadTemporaryProxyRoleIdentity | undefined => {
  if (addressKey === null || activeProfileProxyId !== null) {
    return undefined;
  }
  const proxyCreatorId = jwtIdentity?.addressKey === addressKey ? jwtIdentity.proxyCreatorId : null;
  if (proxyCreatorId === null) {
    return undefined;
  }
  return {
    addressKey,
    proxyCreatorId,
    identityKey: getWaveReadProxyRoleIdentityKey({
      addressKey,
      proxyCreatorId
    })
  };
};
const getVerifiedIdentity = ({
  addressKey,
  activeProfileProxyId,
  activeProfileProxyCreatorId,
  identityKey,
  jwtIdentity,
  verifiedAuthHeaders
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly identityKey: string;
  readonly jwtIdentity: WaveReadJwtIdentity | undefined;
  readonly verifiedAuthHeaders: AuthHeaders | undefined;
}): WaveReadVerifiedIdentity | undefined => {
  if (!verifiedAuthHeaders || addressKey === null || !jwtIdentity) {
    return undefined;
  }
  if (isWaveReadJwtExpired(jwtIdentity.jwtExpiresAt)) {
    return undefined;
  }
  return {
    addressKey,
    activeProfileProxyId,
    activeProfileProxyCreatorId,
    identityKey,
    jwtExpiresAt: jwtIdentity.jwtExpiresAt,
    authHeaders: verifiedAuthHeaders
  };
};
const getProxyRoleIdentityKey = ({
  addressKey,
  activeProfileProxyCreatorId,
  temporaryProxyRoleIdentity
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly temporaryProxyRoleIdentity: WaveReadTemporaryProxyRoleIdentity | undefined;
}): string | null => {
  if (temporaryProxyRoleIdentity) {
    return temporaryProxyRoleIdentity.identityKey;
  }
  if (addressKey === null || activeProfileProxyCreatorId === null) {
    return null;
  }
  return getWaveReadProxyRoleIdentityKey({
    addressKey,
    proxyCreatorId: activeProfileProxyCreatorId
  });
};
export const useWaveReadIdentityState = t0 => {
  const $ = _c(37);
  const {
    address,
    connectedProfileId,
    activeProfileProxyId,
    activeProfileProxyCreatorId,
    walletAuth
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = getAddressKey(address);
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const addressKey = t1;
  let t2;
  if ($[2] !== activeProfileProxyId || $[3] !== connectedProfileId || $[4] !== walletAuth) {
    t2 = decodeWaveReadJwtIdentity(walletAuth, connectedProfileId, activeProfileProxyId);
    $[2] = activeProfileProxyId;
    $[3] = connectedProfileId;
    $[4] = walletAuth;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const jwtIdentity = t2;
  let t3;
  if ($[6] !== activeProfileProxyCreatorId || $[7] !== addressKey || $[8] !== jwtIdentity || $[9] !== walletAuth) {
    t3 = getVerifiedAuthHeaders({
      walletAuth,
      addressKey,
      jwtIdentity,
      activeProfileProxyCreatorId
    });
    $[6] = activeProfileProxyCreatorId;
    $[7] = addressKey;
    $[8] = jwtIdentity;
    $[9] = walletAuth;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const verifiedAuthHeaders = t3;
  let t4;
  if ($[11] !== activeProfileProxyId || $[12] !== addressKey || $[13] !== jwtIdentity) {
    t4 = getTemporaryProxyRoleIdentity({
      addressKey,
      activeProfileProxyId,
      jwtIdentity
    });
    $[11] = activeProfileProxyId;
    $[12] = addressKey;
    $[13] = jwtIdentity;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const temporaryProxyRoleIdentity = t4;
  let t5;
  if ($[15] !== activeProfileProxyId || $[16] !== addressKey || $[17] !== temporaryProxyRoleIdentity?.identityKey) {
    t5 = temporaryProxyRoleIdentity?.identityKey ?? getWaveReadIdentityKey({
      addressKey,
      activeProfileProxyId
    });
    $[15] = activeProfileProxyId;
    $[16] = addressKey;
    $[17] = temporaryProxyRoleIdentity?.identityKey;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const identityKey = t5;
  let t6;
  if ($[19] !== activeProfileProxyCreatorId || $[20] !== addressKey || $[21] !== temporaryProxyRoleIdentity) {
    t6 = getProxyRoleIdentityKey({
      addressKey,
      activeProfileProxyCreatorId,
      temporaryProxyRoleIdentity
    });
    $[19] = activeProfileProxyCreatorId;
    $[20] = addressKey;
    $[21] = temporaryProxyRoleIdentity;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const proxyRoleIdentityKey = t6;
  let t7;
  if ($[23] !== activeProfileProxyCreatorId || $[24] !== activeProfileProxyId || $[25] !== addressKey || $[26] !== identityKey || $[27] !== jwtIdentity || $[28] !== verifiedAuthHeaders) {
    t7 = getVerifiedIdentity({
      addressKey,
      activeProfileProxyId,
      activeProfileProxyCreatorId,
      identityKey,
      jwtIdentity,
      verifiedAuthHeaders
    });
    $[23] = activeProfileProxyCreatorId;
    $[24] = activeProfileProxyId;
    $[25] = addressKey;
    $[26] = identityKey;
    $[27] = jwtIdentity;
    $[28] = verifiedAuthHeaders;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const verifiedIdentity = t7;
  let t8;
  if ($[30] !== activeProfileProxyId || $[31] !== addressKey || $[32] !== identityKey || $[33] !== proxyRoleIdentityKey || $[34] !== temporaryProxyRoleIdentity || $[35] !== verifiedIdentity) {
    t8 = {
      addressKey,
      activeProfileProxyId,
      identityKey,
      proxyRoleIdentityKey,
      temporaryProxyRoleIdentity,
      verifiedIdentity
    };
    $[30] = activeProfileProxyId;
    $[31] = addressKey;
    $[32] = identityKey;
    $[33] = proxyRoleIdentityKey;
    $[34] = temporaryProxyRoleIdentity;
    $[35] = verifiedIdentity;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  return t8;
};